import React, { useEffect, useMemo, useState } from 'react';

const SLOT_PX = 146;
const COPIES = 7;
const STEP_MS = 1100;
const TRANSITION_MS = 320;

const IntegrationsLogoSection = ({
  items = [],
  eyebrow = 'Integrations',
  title = 'Connects to the stack you already have',
  description = 'Auth, product data, email, and billing. Kolanut watches the signals and drafts the outreach.',
}) => {
  const count = items.length;
  const extended = useMemo(
    () => Array.from({ length: COPIES }, () => items).flat(),
    [items]
  );
  const middleIndex = count * Math.floor(COPIES / 2);
  const wrapHigh = count * (COPIES - 2);
  const wrapLow = count * 2;
  const [index, setIndex] = useState(middleIndex);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIndex(count * Math.floor(COPIES / 2));
    setAnimate(true);
  }, [count]);

  useEffect(() => {
    if (paused || count < 2) return undefined;

    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) return undefined;

    const id = window.setInterval(() => {
      setIndex((current) => current + 1);
    }, STEP_MS);

    return () => window.clearInterval(id);
  }, [paused, count]);

  useEffect(() => {
    if (count < 2) return undefined;
    if (index < wrapHigh && index >= wrapLow) return undefined;

    const delay = animate ? TRANSITION_MS : 0;
    const timeout = window.setTimeout(() => {
      setAnimate(false);
      setIndex((current) => {
        if (current >= wrapHigh) return current - count;
        if (current < wrapLow) return current + count;
        return current;
      });
    }, delay);

    return () => window.clearTimeout(timeout);
  }, [index, count, animate, wrapHigh, wrapLow]);

  useEffect(() => {
    if (animate) return undefined;

    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setAnimate(true));
    });

    return () => window.cancelAnimationFrame(frame);
  }, [animate]);

  return (
    <section id="integrations" className="py-16 md:py-20 bg-white overflow-hidden scroll-mt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[11px] sm:text-xs font-medium uppercase tracking-[0.18em] text-gray-400 mb-3">
            {eyebrow}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-[2.5rem] font-normal tracking-tight text-gray-900 leading-[1.15]">
            {title}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-gray-500 leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      <div
        className="relative mt-12 md:mt-14"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="flex flex-col items-center">
          <div className="relative z-20 flex h-[6.25rem] w-[6.25rem] sm:h-[6.5rem] sm:w-[6.5rem] items-center justify-center rounded-[1.75rem] border border-gray-200 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
            <img
              src="/kolanut_logo.png"
              alt="Kolanut"
              className="h-12 w-12 sm:h-14 sm:w-14 object-contain"
            />
          </div>
          <div
            className="relative z-20 h-11 w-px bg-gradient-to-b from-gray-300 to-[#8ae690]"
            aria-hidden
          />
        </div>

        <div className="relative -mt-1 overflow-hidden">
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-32 bg-gradient-to-r from-white to-transparent z-10"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-32 bg-gradient-to-l from-white to-transparent z-10"
            aria-hidden
          />

          <div
            className="flex items-end pb-8 pt-3 will-change-transform"
            style={{
              transform: `translateX(calc(50% - ${SLOT_PX / 2}px - ${index * SLOT_PX}px))`,
              transition: animate
                ? `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
                : 'none',
            }}
          >
            {extended.map((item, itemIndex) => {
              const isActive = itemIndex === index;

              return (
                <button
                  key={`${item.name}-${itemIndex}`}
                  type="button"
                  onClick={() => {
                    let next = itemIndex;
                    while (count > 0 && next >= wrapHigh) next -= count;
                    while (count > 0 && next < wrapLow) next += count;
                    setIndex(next);
                  }}
                  aria-pressed={isActive}
                  aria-label={item.name}
                  className="flex shrink-0 flex-col items-center justify-end"
                  style={{ width: SLOT_PX }}
                >
                  <span
                    className={`flex items-center justify-center rounded-[1.75rem] border transition-all duration-500 ${
                      isActive
                        ? 'h-[6.75rem] w-[6.75rem] sm:h-[7.8rem] sm:w-[7.8rem] border-[#c4ffa0]/70 bg-white shadow-[0_12px_32px_rgba(0,0,0,0.08)]'
                        : 'h-[5.5rem] w-[5.5rem] sm:h-[6.2rem] sm:w-[6.2rem] border-gray-200/80 bg-[#f4f5f7]'
                    }`}
                  >
                    <img
                      src={item.logo}
                      alt=""
                      className={`object-contain transition-all duration-500 ${
                        isActive
                          ? 'h-12 w-12 sm:h-14 sm:w-14 grayscale-0 opacity-100'
                          : 'h-9 w-9 sm:h-10 sm:w-10 grayscale opacity-35'
                      }`}
                    />
                  </span>
                  <span
                    className={`mt-2.5 max-w-[8.5rem] truncate text-center text-xs sm:text-sm font-medium transition-all duration-300 ${
                      isActive ? 'text-gray-800 opacity-100' : 'text-gray-400 opacity-0'
                    }`}
                  >
                    {item.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationsLogoSection;
